// supabaseClient.js
import { createClient } from '@supabase/supabase-js';
import { REST_API_END_POINT } from '../constants/Defaultvalues';
import {
  createUploadValidationError,
  getUploadValidationError,
} from '../utils/fileUploadValidation';

const supabaseUrl = 'https://iesnxcfhhtlsfqnfpwkt.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imllc254Y2ZoaHRsc2ZxbmZwd2t0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTE5NTQ3NjAsImV4cCI6MjA2NzUzMDc2MH0.irsU2aFA29w6jHrGL4d4HFacBg5oCH4LocT2sERXy64';

const ATTACHMENTS_BUCKET = 'signwise-attachments';
const PUBLIC_MARKER = `/storage/v1/object/public/${ATTACHMENTS_BUCKET}/`;

export const supabase = createClient(supabaseUrl, supabaseKey);

function ensureHttpsProxyUrl(url) {
  if (
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:' &&
    typeof url === 'string' &&
    url.startsWith('http://')
  ) {
    return `https://${url.slice('http://'.length)}`;
  }
  return url;
}

function buildProxyAttachmentUrl(path) {
  return ensureHttpsProxyUrl(
    `${REST_API_END_POINT}attachments/file?path=${encodeURIComponent(path)}`
  );
}

/** Keep upload key + public URL in sync — Supabase rejects many special chars that DB/URLs still store as %XX. */
function sanitizeAttachmentStoragePath(path) {
  if (typeof path !== 'string' || !path) return path;
  const parts = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (!parts.length) return path;
  const last = parts.length - 1;
  const cleaned = parts[last].replace(/\s+/g, '_').replace(/[^a-zA-Z0-9.\-_]/g, '');
  parts[last] = cleaned || 'file';
  return parts.join('/');
}

// supabase.storage is a getter (new client each access) — patch prototype once.
const storageFileApiProto = Object.getPrototypeOf(
  supabase.storage.from(ATTACHMENTS_BUCKET)
);
const originalGetPublicUrl = storageFileApiProto.getPublicUrl;
const originalUpload = storageFileApiProto.upload;

storageFileApiProto.upload = function patchedUpload(path, fileBody, fileOptions) {
  const safePath =
    this.bucketId === ATTACHMENTS_BUCKET ? sanitizeAttachmentStoragePath(path) : path;
  const validationError = getUploadValidationError(fileBody, safePath);

  if (validationError) {
    return Promise.resolve({
      data: null,
      error: createUploadValidationError(validationError),
    });
  }

  return originalUpload.call(this, safePath, fileBody, fileOptions);
};

storageFileApiProto.getPublicUrl = function patchedGetPublicUrl(path, options) {
  const safePath =
    this.bucketId === ATTACHMENTS_BUCKET ? sanitizeAttachmentStoragePath(path) : path;
  const result = originalGetPublicUrl.call(this, safePath, options);
  if (this.bucketId === ATTACHMENTS_BUCKET && result?.data) {
    result.data.publicUrl = buildProxyAttachmentUrl(safePath);
  }
  return result;
};

/** Convert a stored public Supabase attachment URL to the secure proxy URL. */
export function toSecureAttachmentUrl(url) {
  if (!url || typeof url !== 'string') return url;
  // Already a proxy URL — just fix mixed-content if needed
  if (url.includes('/webservice/attachments/file?path=')) {
    return ensureHttpsProxyUrl(url);
  }
  const idx = url.indexOf(PUBLIC_MARKER);
  if (idx === -1) return url;
  let path = url.slice(idx + PUBLIC_MARKER.length);
  try {
    path = decodeURIComponent(path);
  } catch (_) {
    /* keep raw */
  }
  return buildProxyAttachmentUrl(path);
}

// Mask remaining direct Supabase URLs used in <img>/<a> (profile pics, logos, etc.)
function patchDomUrlProperty(Constructor, propName) {
  if (typeof window === 'undefined' || !Constructor?.prototype) return;
  const descriptor = Object.getOwnPropertyDescriptor(Constructor.prototype, propName);
  if (!descriptor?.set || !descriptor?.get) return;

  Object.defineProperty(Constructor.prototype, propName, {
    configurable: true,
    enumerable: descriptor.enumerable,
    get() {
      return descriptor.get.call(this);
    },
    set(value) {
      descriptor.set.call(this, toSecureAttachmentUrl(value));
    },
  });
}

function installAttachmentUrlMasking() {
  if (typeof window === 'undefined' || window.__signwiseAttachmentUrlMaskInstalled) return;
  window.__signwiseAttachmentUrlMaskInstalled = true;

  patchDomUrlProperty(window.HTMLImageElement, 'src');
  patchDomUrlProperty(window.HTMLSourceElement, 'src');
  patchDomUrlProperty(window.HTMLVideoElement, 'src');
  patchDomUrlProperty(window.HTMLAudioElement, 'src');
  patchDomUrlProperty(window.HTMLAnchorElement, 'href');

  const originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function setAttributeMasked(name, value) {
    if (
      (name === 'src' || name === 'href') &&
      typeof value === 'string' &&
      value.includes(PUBLIC_MARKER)
    ) {
      value = toSecureAttachmentUrl(value);
    }
    return originalSetAttribute.call(this, name, value);
  };
}

installAttachmentUrlMasking();
